import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { chatbotService, type BotSource } from 'api/services/chatbotService';

// Widget de chatbot de ayuda. Autocontenido (estilos inline) para poder replicarlo
// en otros sistemas: solo depende de chatbotService (que a su vez usa el cliente HTTP).
// Ver documentacion/arquitectura_chatbot.md

type Msg = { id?: number; role: 'user' | 'bot'; content: string; sources?: BotSource[] };
const CONV_KEY = 'bot-conversation-key';

export default function ChatWidget({
  title = 'Asistente de ayuda',
  accent = '#0d6efd',
  welcome = 'Hola, soy el asistente de ayuda. Pregúntame cómo usar la plataforma (obras, visor 3D, tu cuenta, etc.).',
}: { title?: string; accent?: string; welcome?: string }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([{ role: 'bot', content: welcome }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const convKey = useRef<string | null>(localStorage.getItem(CONV_KEY));
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollDown = () => { requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }); };
  useEffect(() => { if (open) scrollDown(); }, [messages, open]);

  // Cargar historial la primera vez que se abre (si ya había conversación)
  useEffect(() => {
    if (!open || loaded) return;
    setLoaded(true);
    const key = convKey.current;
    if (!key) return;
    chatbotService.history(key)
      .then((r) => {
        if (r.messages.length) {
          setMessages(r.messages.map((m) => ({ id: m.id, role: m.role, content: m.content })));
        }
      })
      .catch(() => { /* sin historial */ });
  }, [open, loaded]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await chatbotService.send(text, convKey.current);
      convKey.current = res.conversationKey;
      localStorage.setItem(CONV_KEY, res.conversationKey);
      setMessages((m) => [...m, { id: res.idMessage, role: 'bot', content: res.reply, sources: res.sources }]);
    } catch {
      setMessages((m) => [...m, { role: 'bot', content: 'Ocurrió un error al procesar tu mensaje. Intenta de nuevo.' }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const bubble: CSSProperties = {
    position: 'fixed', bottom: 24, right: 24, width: 56, height: 56, borderRadius: '50%',
    background: accent, color: '#fff', border: 'none', cursor: 'pointer', zIndex: 1055,
    boxShadow: '0 6px 20px rgba(0,0,0,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
  };
  const panel: CSSProperties = {
    position: 'fixed', bottom: 24, right: 24, width: 360, maxWidth: 'calc(100vw - 32px)', height: 520, maxHeight: 'calc(100vh - 48px)',
    background: '#fff', borderRadius: 14, zIndex: 1056, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(0,0,0,.28)', border: '1px solid rgba(0,0,0,.08)',
  };

  if (!open) {
    return <button style={bubble} onClick={() => setOpen(true)} title={title} aria-label={title}><i className="ph ph-chat-circle-dots" /></button>;
  }

  return (
    <div style={panel}>
      {/* Header */}
      <div style={{ background: accent, color: '#fff', padding: '.7rem .9rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.5rem', fontWeight: 600 }}>
          <i className="ph ph-chat-circle-dots" style={{ fontSize: 20 }} />{title}
        </span>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 20, display: 'flex' }} aria-label="Cerrar"><i className="ph ph-x" /></button>
      </div>

      {/* Mensajes */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '.8rem', background: '#f5f7fb', display: 'flex', flexDirection: 'column', gap: '.55rem' }}>
        {messages.map((m, i) => (
          <div key={m.id ?? i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
            <div style={{
              padding: '.5rem .7rem', borderRadius: 12, fontSize: '.85rem', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              background: m.role === 'user' ? accent : '#fff', color: m.role === 'user' ? '#fff' : '#1f2937',
              border: m.role === 'user' ? 'none' : '1px solid rgba(0,0,0,.08)',
              borderBottomRightRadius: m.role === 'user' ? 3 : 12, borderBottomLeftRadius: m.role === 'user' ? 12 : 3,
            }}>{m.content}</div>
            {m.sources && m.sources.length > 0 && (
              <div style={{ fontSize: '.68rem', color: '#6b7280', marginTop: 3, paddingLeft: 2 }}>
                <i className="ph ph-book-open" style={{ marginRight: 3 }} />{m.sources.map((s) => s.title).join(' · ')}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div style={{ alignSelf: 'flex-start', padding: '.5rem .7rem', borderRadius: 12, background: '#fff', border: '1px solid rgba(0,0,0,.08)', fontSize: '.85rem', color: '#9ca3af' }}>
            <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, marginRight: 6 }} />escribiendo…
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{ padding: '.6rem', borderTop: '1px solid rgba(0,0,0,.08)', display: 'flex', gap: '.4rem', flexShrink: 0, background: '#fff' }}>
        <textarea
          value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} rows={1}
          placeholder="Escribe tu pregunta…"
          style={{ flex: 1, resize: 'none', border: '1px solid #d1d5db', borderRadius: 10, padding: '.45rem .6rem', fontSize: '.85rem', outline: 'none', maxHeight: 80, fontFamily: 'inherit' }}
        />
        <button onClick={send} disabled={sending || !input.trim()} title="Enviar"
          style={{ background: accent, color: '#fff', border: 'none', borderRadius: 10, width: 40, cursor: sending || !input.trim() ? 'default' : 'pointer', opacity: sending || !input.trim() ? 0.5 : 1, fontSize: 18, flexShrink: 0 }}>
          <i className="ph ph-paper-plane-right" />
        </button>
      </div>
    </div>
  );
}
